import { SocketSessionError } from '@common/sockets/enums/socket-session-error.enum';
import { WorkspaceFilesGetRequest, WorkspaceFilesGetResponse } from '@common/sockets/models/workspace-files-get.model';
import { SocketEmitterError } from '@common/sockets/socket-emitter';
import { SessionOptions, SocketSession } from '@common/sockets/socket-session';
import { WorkspaceSessionErrorEvent } from '@common/sockets/types/workspace-session-error-event.type';
import { isEnumValue } from '@common/util/enum';
import { ManagerOptions, SocketOptions } from 'socket.io-client';

export interface WorkspaceSessionOptions extends SessionOptions {
  branchName: string;
  projectId: number;
}

/**
 * WorkspaceSession
 */
export class WorkspaceSession extends SocketSession<WorkspaceSessionOptions, WorkspaceSessionErrorEvent> {
  public branchName: string;
  public projectId: number;

  private workspaceId: string;

  constructor(options: WorkspaceSessionOptions) {
    super(options);

    this.branchName = options.branchName ?? '';
    this.projectId = options.projectId ?? null;
  }

  protected override createSocketOptions(socketUrl: URL): Partial<ManagerOptions & SocketOptions> {
    return {
      ...super.createSocketOptions(socketUrl),
      query: {
        branchName: this.branchName,
        projectId: this.projectId
      }
    }
  }

  protected override parseError(event: string, error: any): WorkspaceSessionErrorEvent {
    if ((event === 'connect_error' || event === 'reconnect_error') &&
      error.data && isEnumValue(SocketSessionError, error.data.name)) {
      return { type: error.data.name as SocketSessionError, error: error.data };
    }

    return { type: SocketSessionError.UnableToReachServer, error };
  }

  /** Causes the session to fetch all the workspace files. */
  public reload() {
    this.loadWorkspaceFiles();
  }

  /** Creates a session with the server by opening a socket connection and setting up event handlers. */
  public start() {
    super.start();

    // Listen to the ready event and load the workspace data
    this.socket.on('ready', data => {
      // Store the workspaceId for future communication with the server
      this.workspaceId = data.workspaceId;
      this.loadWorkspaceFiles();
    });

    // Listen to workspace file deleted event and emit it
    this.socket.on('workspace.file.deleted', data => {
      this.emit('workspace.file.deleted', data);
    });

    // Listen to workspace file inserted event and emit it
    this.socket.on('workspace.file.inserted', data => {
      this.emit('workspace.file.inserted', data);
    });

    // Listen to workspace file updated event and emit it
    this.socket.on('workspace.file.updated', data => {
      this.emit('workspace.file.updated', data);
    });

    // Listen to workspace file renamed event and emit it
    this.socket.on('workspace.file.renamed', data => {
      this.emit('workspace.file.renamed', data);
    });

    // Listen to workspace doc deleted event and emit it. This is distinct from `workspace.file.deleted`
    // which fires from the Mongo change stream for workspace_file row deletes (discards). `workspace.doc.deleted`
    // fires from collab-authority's pub/sub for explicit collab delete operations so clients can drop the node
    // from the file tree regardless of gitStatus.
    this.socket.on('workspace.doc.deleted', data => {
      this.emit('workspace.doc.deleted', data);
    });

    // Listen to workspace doc merged event and emit it. Fires from collab-authority's pub/sub when
    // a doc is merged with an incoming commit (auto-pull or manual pull). Distinct from
    // `workspace.file.updated` because a merge of a clean doc may not touch the workspace_files row
    // and therefore not produce a Mongo change stream event — listeners that need to react to merged
    // content (e.g. reloading computed stylesheets) should subscribe here as well.
    this.socket.on('workspace.doc.merged', data => {
      this.emit('workspace.doc.merged', data);
    });

    // Workspace pull progress events. The pull runs in collab-pull but the events are bridged to
    // the workspace socket room so clients in the workspace receive per-file progress updates.
    this.socket.on('workspace.pull.started', data => {
      this.emit('workspace.pull.started', data);
    });
    this.socket.on('workspace.pull.progress', data => {
      this.emit('workspace.pull.progress', data);
    });
    this.socket.on('workspace.pull.completed', data => {
      this.emit('workspace.pull.completed', data);
    });
    this.socket.on('workspace.pull.failed', data => {
      this.emit('workspace.pull.failed', data);
    });
    this.socket.on('workspace.pull.file-completed', data => {
      this.emit('workspace.pull.file-completed', data);
    });
  }

  /** Loads the workspace files from the server. */
  private loadWorkspaceFiles() {
    this.emitter.emit<WorkspaceFilesGetRequest, WorkspaceFilesGetResponse>('workspace.files.get', {
      workspaceId: this.workspaceId
    }, this.retryEmit.bind(this)).then(data => {
      this.emit('workspace.files.loaded', {
        workspaceFiles: data.workspaceFiles
      });

      // Clear out an existing error now that all the files are loaded
      this.error = null;
    }).catch(error => {
      this.onServerError(error);
    });
  }

  /** Returns true if the SocketEmitter should retry emitting a message when an error occurs. */
  private retryEmit(error: Error): boolean {
    // These errors will be handled by the session and the message should not be re-emitted
    if (error.name === SocketSessionError.AccessDenied || error.name === SocketSessionError.BadRequest) {
      return false;
    }

    return true;
  }

  /** Handles server errors. */
  private onServerError(error: Error) {
    let handled = true;

    // If the user does not have access
    if (error.name === SocketSessionError.AccessDenied) {
      this.stop();
    }
    // If the server was unable to accept the connection because it received invalid parameters
    else if (error.name === SocketSessionError.BadRequest) {
      this.stop();
    }
    // If these are expected errors from the SocketEmitter that can happen under normal operations
    else if (error.name === SocketEmitterError.AlreadyEmittingMessage || error.name === SocketEmitterError.EmissionCanceled) {
      // Do nothing
      handled = false;
    }
    // Else the error was not handled
    else {
      handled = false;
    }

    if (handled) {
      this.error = { type: error.name as SocketSessionError, error };
    }
  }
}
